import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, XCircle } from "lucide-react";
import { JP } from "@/components/JP";
import { BatchBreak } from "@/components/BatchBreak";
import { Mascot } from "@/components/Mascot";
import { usePrefs } from "@/context/PrefsContext";
import { api } from "@/lib/api";

function Passage({ q, t, bilingual }) {
  const [open, setOpen] = useState(bilingual === "stacked");
  if (!q.passage_jp) return null;
  return (
    <div data-testid="question-passage" className="soft-card mb-6 p-5 sm:p-7">
      <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">{t("passage")}</p>
      <JP text={q.passage_jp} as="p" className="text-[17px]" />
      {open && <p data-testid="passage-translation" className="mt-4 border-t border-border pt-4 text-sm leading-relaxed text-muted-foreground">{q.passage_id}</p>}
      <button data-testid="passage-translation-toggle" onClick={() => setOpen(!open)} className="mt-3 text-xs font-medium text-secondary underline-offset-4 hover:underline">{open ? t("hideTranslation") : t("showTranslation")}</button>
    </div>
  );
}

function Feedback({ q, chosen, mascots, t }) {
  const ok = chosen === q.answer;
  const m = mascots?.find((x) => x.code === (ok ? "senang" : "bingung"));
  return (
    <div data-testid="instant-feedback" data-correct={ok} className={`pop-in mt-6 flex items-start gap-4 rounded-2xl border p-4 ${ok ? "border-sage/40 bg-sage-soft/60" : "border-primary/30 bg-primary/5"}`}>
      {m && <Mascot m={m} size={64} message={false} testid="feedback-mascot" />}
      <div className="min-w-0">
        <p className={`flex items-center gap-1.5 text-sm font-semibold ${ok ? "text-sage" : "text-primary"}`}>{ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{ok ? t("correct") : t("wrong")}</p>
        {!ok && <p className="mt-1 text-sm"><span className="text-muted-foreground">{t("correctAnswer")}: </span><span lang="ja" className="jp font-medium">{q.options.find((o) => o.key === q.answer)?.text}</span></p>}
        {q.explanation_id && <p className="mt-1 text-sm text-muted-foreground" data-testid="feedback-explanation"><span className="font-medium text-foreground">{t("explanation")}: </span>{q.explanation_id}</p>}
      </div>
    </div>
  );
}

const LETTERS = "ABCDEF";

export function QuestionRunner({ questions, onSubmit, submitting, batchSize, onCheckpoint, instant = false, initialAnswers, initialIdx = 0, onProgress }) {
  const { t, prefs, area } = usePrefs();
  const [idx, setIdx] = useState(Math.min(initialIdx, questions.length - 1));
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [showTr, setShowTr] = useState(false);
  const [brk, setBrk] = useState(null);
  const [mascots, setMascots] = useState(null);
  const q = questions[idx];
  const total = questions.length;
  const chosen = answers[q.id];
  const last = idx === total - 1;
  const pct = Math.round(((idx + (chosen ? 1 : 0)) / total) * 100);
  const translationVisible = prefs.bilingual === "stacked" || showTr;
  const batches = batchSize ? Math.ceil(total / batchSize) : 1;
  const batchNo = batchSize ? Math.floor(idx / batchSize) + 1 : 1;
  const batchEnd = batchSize && !last && (idx + 1) % batchSize === 0;
  const locked = instant && Boolean(chosen);

  useEffect(() => { if (instant) api.get("/mascots").then((r) => setMascots(r.data)).catch(() => {}); }, [instant]);
  useEffect(() => { onProgress?.(answers, idx); }, [answers, idx, onProgress]);

  const go = (n) => { setIdx(n); setShowTr(false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const pick = (key) => { if (locked) return; setAnswers({ ...answers, [q.id]: key }); };
  const checkpoint = async () => {
    const subset = Object.fromEntries(questions.slice(idx + 1 - batchSize, idx + 1).map((x) => [x.id, answers[x.id]]).filter(([, v]) => v));
    setBrk({ loading: true });
    const review = await onCheckpoint(subset);
    setBrk({ review });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const optState = (o) => {
    if (!locked) return undefined;
    if (o.key === q.answer) return "correct";
    if (o.key === chosen) return "wrong";
    return "dim";
  };

  if (brk) return <BatchBreak review={brk.review} loading={brk.loading} batchNo={batchNo} batches={batches} done={idx + 1} total={total} onContinue={() => { setBrk(null); go(idx + 1); }} />;

  return (
    <div className="mx-auto max-w-2xl fade-up" key={q.id}>
      <div className="mb-8">
        <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid="question-counter">{t("question")} {idx + 1} {t("of")} {total}{batchSize ? <span data-testid="batch-indicator"> · {t("batch")} {batchNo}/{batches}</span> : null}</span>
          <span className="rounded-full bg-muted px-2.5 py-0.5 font-medium">{q.level} · {area(q.type)}</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div data-testid="question-progress" className="h-full rounded-full bg-sage transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      </div>

      <Passage q={q} t={t} bilingual={prefs.bilingual} />

      <JP text={q.prompt_jp} as="h2" className="text-2xl sm:text-[28px] font-medium" data-testid="question-prompt" />
      {translationVisible ? (
        <p data-testid="question-translation" className="mt-3 text-sm text-muted-foreground">{q.prompt_id}</p>
      ) : (
        <button data-testid="question-translation-toggle" onClick={() => setShowTr(true)} className="mt-3 text-xs font-medium text-secondary underline-offset-4 hover:underline">{t("showTranslation")}</button>
      )}

      <div className="mt-8 grid gap-3" data-testid="question-options">
        {q.options.map((o, i) => (
          <button key={o.key} data-testid={`option-${o.key}`} data-position={LETTERS[i]} data-selected={chosen === o.key} data-state={optState(o)} disabled={locked} onClick={() => pick(o.key)} className="option-card flex items-center gap-4 disabled:cursor-default">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${chosen === o.key ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>{LETTERS[i]}</span>
            <span lang="ja" className="jp text-lg">{o.text}</span>
          </button>
        ))}
      </div>

      {locked && <Feedback q={q} chosen={chosen} mascots={mascots} t={t} />}

      <div className="mt-10 flex items-center justify-between gap-3">
        <button data-testid="question-prev-button" disabled={idx === 0} onClick={() => go(idx - 1)} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent disabled:opacity-30"><ArrowLeft className="h-4 w-4" />{t("prev")}</button>
        {last ? (
          <button data-testid="question-submit-button" disabled={!chosen || submitting} onClick={() => onSubmit(answers)} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-[transform,opacity] hover:-translate-y-0.5 disabled:opacity-40"><Check className="h-4 w-4" />{t("submit")}</button>
        ) : batchEnd ? (
          <button data-testid="question-batch-button" disabled={!chosen} onClick={checkpoint} className="inline-flex items-center gap-2 rounded-full bg-secondary px-6 py-3 text-sm font-semibold text-white transition-[transform,opacity] hover:-translate-y-0.5 disabled:opacity-40">{t("batchReview")}<ArrowRight className="h-4 w-4" /></button>
        ) : (
          <button data-testid="question-next-button" disabled={!chosen} onClick={() => go(idx + 1)} className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background transition-[transform,opacity] hover:-translate-y-0.5 disabled:opacity-40">{t("next")}<ArrowRight className="h-4 w-4" /></button>
        )}
      </div>
    </div>
  );
}
